import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/feedback/alert-dialog';
import { Database, Download, Trash2, Loader2, FileJson, FileSpreadsheet, Upload } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { fetchActivities, fetchUserProfile } from '@/lib/api';
import { clearAllData } from '@/lib/storage';
import { auth } from '@/lib/auth';
import { ActivityUploadSection } from './ActivityUploadSection';
import { ImportSeasonModal } from '@/components/calendar/modals/ImportSeasonModal';

export function DataManagementSection() {
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');
  const [importSeasonOpen, setImportSeasonOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const [activities, profile] = await Promise.all([fetchActivities({ limit: 1000 }), fetchUserProfile()]);
      const exportData = { profile: profile || null, activities, exportDate: new Date().toISOString(), version: '1.0' };
      let blob: Blob; let filename: string;
      if (exportFormat === 'json') {
        blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        filename = `athlete-space-export-${new Date().toISOString().split('T')[0]}.json`;
      } else {
        type ProfileWithName = { name?: string; full_name?: string };
        const profileName = (profile as ProfileWithName | null)?.name ?? (profile as ProfileWithName | null)?.full_name ?? 'N/A';
        const csvRows: string[] = ['Section,Field,Value', `Profile,Name,"${profileName}"`, ''];
        csvRows.push('Activity ID,Date,Sport,Title,Duration,Distance,Load');
        activities.forEach(a => csvRows.push(`${a.id},"${a.date}",${a.sport},"${a.title}",${a.duration},${a.distance},${a.trainingLoad}`));
        blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        filename = `athlete-space-export-${new Date().toISOString().split('T')[0]}.csv`;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
      toast({ title: 'Data exported', description: `Exported as ${exportFormat.toUpperCase()}` });
    } catch (error) { toast({ title: 'Export failed', description: error instanceof Error ? error.message : 'Could not export', variant: 'destructive' }); }
    finally { setIsExporting(false); }
  };

  const handleDeleteAllData = async () => {
    setIsDeleting(true);
    try {
      await queryClient.cancelQueries();
      clearAllData();
      localStorage.removeItem('strava_last_sync');
      localStorage.removeItem('theme');
      auth.clear();
      queryClient.removeQueries();
      queryClient.clear();
      toast({ title: 'Data deleted', description: 'All data has been deleted. Redirecting...' });
      setTimeout(() => navigate('/onboarding', { replace: true }), 100);
    } catch (error) { toast({ title: 'Delete failed', description: error instanceof Error ? error.message : 'Could not delete', variant: 'destructive' }); setIsDeleting(false); }
  };

  const handleSeasonImported = () => {
    queryClient.invalidateQueries({ queryKey: ['calendarWeek'] });
    queryClient.invalidateQueries({ queryKey: ['calendarSeason'] });
    queryClient.invalidateQueries({ queryKey: ['calendarToday'] });
  };

  const hudBtnClass = 'flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200 border border-[rgba(0,255,200,0.3)] bg-[rgba(0,255,200,0.08)] text-[#00FFC8] hover:bg-[rgba(0,255,200,0.15)] hover:shadow-[0_0_16px_rgba(0,255,200,0.15)]';

  return (
    <div className="space-y-4">
      {/* Activity Upload */}
      <ActivityUploadSection />

      {/* Import Season */}
      <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
            <Upload className="h-4 w-4 text-[#00FFC8]" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground">Import Season</h3>
            <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">BULK SESSION IMPORT</p>
          </div>
        </div>
        <p className="text-[10px] font-mono text-[#A0A0A0] mb-3">Import a full season of training sessions from a JSON file.</p>
        <button onClick={() => setImportSeasonOpen(true)} className={hudBtnClass}>
          <Upload className="h-3.5 w-3.5" /> IMPORT SEASON
        </button>
      </div>

      {/* Data Export & Delete */}
      <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
            <Database className="h-4 w-4 text-[#00FFC8]" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground">Data Management</h3>
            <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">EXPORT & PURGE</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Export */}
          <div className="space-y-3">
            <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-white/30 block">EXPORT FORMAT</span>
            <div className="flex gap-2">
              {[
                { value: 'json' as const, label: 'JSON', icon: FileJson },
                { value: 'csv' as const, label: 'CSV', icon: FileSpreadsheet },
              ].map((fmt) => (
                <button
                  key={fmt.value}
                  type="button"
                  onClick={() => setExportFormat(fmt.value)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-md border font-mono text-[10px] tracking-wider transition-all',
                    exportFormat === fmt.value
                      ? 'border-[#00FFC8]/30 bg-[#00FFC8]/[0.06] text-[#00FFC8]'
                      : 'border-white/[0.08] bg-white/[0.02] text-[#A0A0A0] hover:border-white/[0.15]'
                  )}
                >
                  <fmt.icon className="h-3.5 w-3.5" /> {fmt.label}
                </button>
              ))}
            </div>
            <button onClick={handleExportData} disabled={isExporting} className={cn(hudBtnClass, 'w-full', isExporting && 'opacity-50 pointer-events-none')}>
              {isExporting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> EXPORTING...</> : <><Download className="h-3.5 w-3.5" /> EXPORT DATA</>}
            </button>
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* Delete */}
          <div className="space-y-3">
            <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-[hsl(0,70%,55%)] block">PURGE LOCAL DATA</span>
            <p className="text-[10px] font-mono text-[#A0A0A0]">Permanently delete all local training data. This action cannot be undone.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button disabled={isDeleting} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200 border border-[hsl(0,70%,55%)]/30 bg-[hsl(0,70%,55%)]/[0.08] text-[hsl(0,70%,55%)] hover:bg-[hsl(0,70%,55%)]/15 disabled:opacity-40">
                  {isDeleting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> DELETING...</> : <><Trash2 className="h-3.5 w-3.5" /> DELETE ALL LOCAL DATA</>}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-mono">Delete All Local Data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all cached activities, local preferences, and stored profile information.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="font-mono text-[10px] tracking-wider">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAllData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-mono text-[10px] tracking-wider">Delete All Data</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <ImportSeasonModal open={importSeasonOpen} onOpenChange={setImportSeasonOpen} onSuccess={handleSeasonImported} />
    </div>
  );
}
